import { toBackendItem } from './mappers/backend-item';
import { fromBoard, toBoard } from './mappers/board';
import { toItem } from './mappers/item';
import { parseBoards, toError } from './parsers';
import { decodeBoardId } from './utils';
import { sendUploadCoverEvent } from './bi';
import { getCurrentTime } from '../../utils/utils';
import { MediaType } from '../../constants/MediaType';
export function initItems(settings, http) {
  function mapItemList(res, thumbnailSizes) {
    res.data = (res.data.items || []).map(function (data) {
      return toItem(data, settings, thumbnailSizes);
    });
    return res;
  }
  const defaultPaging = {
    size: 50,
    cursor: null
  };
  const defaultSort = {
    order: 'date',
    direction: 'desc'
  };
  function listItems(boardId, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const params = {
      page_size: paging.size || settings.itemsLimit,
      cursor: paging.cursor || null,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order
    };
    if (options.mediaType) {
      params.media_type = options.mediaType.split(',').concat(['folder']).join(',');
    }
    return http.get(`${settings.apiUrl}/go/boards/${boardId}/refs`, params, {
      withCredentials: true,
      headers: options.headers || {}
    }).then(response => {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapItemList(response, options.thumbnails);
    }).catch(toError);
  }
  function addItems(boardIds, items, options) {
    options = options || {};
    const url = `${settings.apiUrl}/v3/boards/refs`;
    const data = {
      boards: boardIds.map(boardId => decodeBoardId(boardId)[1]),
      items: items.map(toBackendItem)
    };
    const postOptions = {
      headers: options.headers || {},
      withCredentials: true
    };
    return http.post(url, data, null, postOptions).then(response => mapItemList(response, options.thumbnails)).catch(toError);
  }
  function deleteItems(boardId, itemsIds, options) {
    options = options || {};
    const url = `${settings.apiUrl}/v3/boards/${decodeBoardId(boardId)[1]}/refs`;
    const data = {
      items: itemsIds
    };
    const postOptions = {
      headers: options.headers || {},
      withCredentials: true
    };
    return http.del(url, data, null, postOptions).catch(toError);
  }
  function create(board, options) {
    if (!board || !board.name) {
      return toError({
        status: -200,
        statusText: 'Internal JS Error',
        data: {
          message: 'Please specify name'
        }
      });
    }
    options = options || {};
    const url = `${settings.apiUrl}/v3/boards`;
    const data = fromBoard(board);
    const postOptions = {
      headers: options.headers || {},
      withCredentials: true
    };
    return http.post(url, data, null, postOptions).then(response => {
      const boardId = response.data && response.data.board_id;
      if (boardId && board.fileThumbnail) {
        return uploadCover(boardId, board.fileThumbnail).then(fileUrl => http.put(url, {
          ...response.data,
          cover_url: fileUrl
        }, null, postOptions));
      }
      return response;
    }).then(response => toBoard(response.data)).catch(toError);
  }
  function getCoverUploadUrl(boardId, file, options) {
    if (options === void 0) {
      options = {};
    }
    return http.get(`${settings.apiUrl}/v3/boards/${boardId}/upload/url`, {
      file_name: file.name.toLowerCase(),
      media_type: MediaType.Picture
    }, {
      withCredentials: true,
      headers: options.headers || {}
    }).then(response => response.data).catch(toError);
  }
  function uploadCover(boardId, file) {
    const uploadStartedAt = getCurrentTime();
    return getCoverUploadUrl(boardId, file).then(coverResponse => {
      const {
        upload_token,
        upload_url
      } = coverResponse;
      return http.upload(upload_url, {
        file,
        upload_token
      });
    }).then(uploadResponse => {
      const uploadFinishedAt = getCurrentTime();
      const {
        file_url: fileId,
        file_size: fileSize
      } = uploadResponse.data[0];
      sendUploadCoverEvent({
        isSuccess: true,
        boardId,
        fileId,
        responseTime: uploadFinishedAt - uploadStartedAt,
        responseSize: fileSize
      });
      return fileId;
    }).catch(err => {
      const uploadFinishedAt = getCurrentTime();
      sendUploadCoverEvent({
        isSuccess: false,
        boardId,
        responseTime: uploadFinishedAt - uploadStartedAt
      });
      throw new Error(err);
    });
  }
  function update(boardId, board, options) {
    if (!board || !board.name) {
      return toError({
        status: -200,
        statusText: 'Internal JS Error',
        data: {
          message: 'Please specify name'
        }
      });
    }
    options = options || {};
    const url = `${settings.apiUrl}/v3/boards`;
    const data = fromBoard(board);
    const postOptions = {
      headers: options.headers || {},
      withCredentials: true
    };
    const updatePromise = board.fileThumbnail ? uploadCover(data.board_id, board.fileThumbnail).then(fileUrl => http.put(url, {
      ...data,
      cover_url: fileUrl
    }, null, postOptions)) : http.put(url, data, null, postOptions);
    return updatePromise.then(response => toBoard(response.data)).catch(toError);
  }
  function list(folderId, options) {
    const [, boardId] = decodeBoardId(folderId);
    if (boardId) {
      // fetching list of board items
      return listItems(boardId, options);
    }
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const requestOptions = {
      page_size: paging.size || settings.itemsLimit,
      cursor: paging.cursor || null,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order,
      is_team: settings.isTeam || options.isTeam
    };
    return http.get(`${settings.apiUrl}/go/boards`, requestOptions, {
      withCredentials: true,
      headers: options.headers || {}
    }).then(response => parseBoards(response.data)).catch(toError);
  }
  function remove(boardIds, options) {
    options = options || {};
    const url = `${settings.apiUrl}/v3/boards`;
    const postOptions = {
      withCredentials: true,
      headers: options.headers || {}
    };
    return http.del(url, {
      boards: boardIds.map(boardId => decodeBoardId(boardId)[1])
    }, null, postOptions).then(() => ({
      id: boardIds
    })).catch(toError);
  }
  return {
    list,
    remove,
    create,
    update,
    addItems,
    listItems,
    deleteItems
  };
}